import React, { useEffect, useState } from 'react'

import { useBottomScrollListener } from 'react-bottom-scroll-listener'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useSubmit } from 'react-router-dom'

import ControlArea from '@components/myScores/ControlArea'
import AddToCollectionButton from '@components/myScores/ControlArea/AddToCollectionButton'
import CopyToSiteButton from '@components/myScores/ControlArea/CopyToSiteButton'
import DeleteScoresButton from '@components/myScores/ControlArea/DeleteScoresButton'
import MyScoresEmptyState from '@components/myScores/MyScoresEmptyState'
import OpenArchivedScoreDialog from '@components/myScores/OpenArchivedScoreDialog'
import ScoresView from '@components/myScores/ScoresView'
import LoadingSpinner from '@components/shared/LoadingSpinner'
import NonIdealState from '@components/shared/NonIdealState'
import Tag from '@components/shared/Tag'
import { isProduct } from '@models/util/product.util'
import { SCORE_PAGE_SIZE } from '@pages/home/loaders/myScores.loader'
import SelectionProvider from '@providers/SelectionProvider'
import { useDialogStore } from '@stores/dialog.store'
import ApiRoutes from '@utils/api-routes'
import http from '@utils/http'
import Storage from '@utils/storage'

import type { ScoreViewOptions } from '@components/myScores/utils/types'
import type { PrivateScoresResponse } from '@pages/home/loaders/myScores.loader'
import type { ViewType } from '@utils/global'

interface Props {
  initialData: PrivateScoresResponse
  order: string | null
  filters: string[] | null
}
const MyScoresView: React.FC<Props> = ({ initialData, order, filters }) => {
  const { t } = useTranslation()
  const openDialog = useDialogStore((s) => s.openDialog)
  const [searchParams] = useSearchParams()
  const submit = useSubmit()

  const [loading, setLoading] = useState(false)
  const [scoresData, setScoresData] = useState(initialData)
  const [layoutType, setLayoutType] = useState(Storage.scoresViewType)
  const setViewType = (type: ViewType): void => {
    setLayoutType(type)
    Storage.scoresViewType = type
  }

  const { scores, remaining, startIndex } = scoresData

  // Used to detect when to infinite scroll
  const scrollRef = useBottomScrollListener<HTMLDivElement>(
    async () => {
      if (!remaining || loading) return

      setLoading(true)
      const [newResp] = await http.post<PrivateScoresResponse>(
        ApiRoutes.members.privateScoresPath(),
        {
          order,
          filters,
          max_count: SCORE_PAGE_SIZE,
          start_index: startIndex,
        }
      )

      if (newResp?.data) {
        setScoresData((prevData) => ({
          ...newResp.data,
          scores: [...prevData.scores, ...newResp.data.scores],
        }))
      }
      setLoading(false)
    },
    { triggerOnNoScroll: true, offset: 32 }
  )

  // Reset the data when we get something new loaded
  useEffect(() => {
    setScoresData(initialData)
  }, [initialData])

  if (!scores.length && !filters?.length) {
    return <MyScoresEmptyState />
  }

  const options: ScoreViewOptions = {
    beforeNavigate: (score) => {
      if (isProduct(score) || !score.archived) return true

      openDialog('open_archived', {
        state: { external_id: score.external_id },
      })
      return false
    },
  }
  const query = searchParams.get('query')

  return (
    <SelectionProvider initialItems={scores}>
      <ControlArea
        viewType={layoutType}
        setViewType={setViewType}
        order={order}
        filters={filters}
        showFiltering
        additionalActions={
          <>
            {query && (
              <Tag
                name={query}
                clickHandler={() => {
                  searchParams.delete('query')
                  submit(searchParams)
                }}
                className="ml-2.5"
              />
            )}
          </>
        }>
        <DeleteScoresButton />
        <AddToCollectionButton />
        <CopyToSiteButton />
      </ControlArea>
      <div className="flex-1 overflow-y-auto w-full pb-4" ref={scrollRef}>
        <ScoresView scores={scores} viewType={layoutType} options={options} />
        {scores.length === 0 && (
          <NonIdealState
            title={t('content.empty.scoresFilter.title')}
            subtitle={t('content.empty.scoresFilter.subtitle')}
            icon="myscores"
          />
        )}
        {remaining && loading && <LoadingSpinner />}
      </div>

      <OpenArchivedScoreDialog />
    </SelectionProvider>
  )
}

export default MyScoresView
